/**
 * Standard page header. Every "top-level" page should use this so
 * titles, subtitles, breadcrumbs and the right-side action area
 * lay out the same way everywhere.
 *
 * Why a shared component: the audit found pages re-implementing
 * their own header with different padding (py-4 vs py-6), different
 * title sizes (text-4xl vs text-3xl vs text-2xl), and different
 * background treatments. That makes navigation feel different on
 * every screen for no real reason.
 *
 * Usage:
 *   <PageHeader
 *     title="Demand Forecasting"
 *     subtitle="Plan demand across SKUs and locations"
 *     actions={<Button>Run forecast</Button>}
 *   />
 */
import React from "react";
import { cn } from "@/lib/utils";


export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  /** Optional kicker chip / label above the title (e.g. "Study"). */
  eyebrow?: React.ReactNode;
  /** Right-side action slot. Typically one to three Buttons. */
  actions?: React.ReactNode;
  /** Renders below the subtitle, above the divider. Use for tabs
   *  or filter chips that should sit inside the header band. */
  beneath?: React.ReactNode;
  /** Drops the bottom divider — useful when the next section
   *  carries its own visual break. */
  bare?: boolean;
  className?: string;
}


export function PageHeader({
  title, subtitle, eyebrow, actions, beneath, bare, className,
}: PageHeaderProps) {
  return (
    <header
      className={cn(
        "px-6 py-5 bg-card",
        !bare && "border-b border-border/60",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          {eyebrow && (
            <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {eyebrow}
            </div>
          )}
          <h1 className="text-2xl font-semibold tracking-tight text-foreground truncate">
            {title}
          </h1>
          {subtitle && (
            <p className="text-sm text-muted-foreground line-clamp-2 max-w-3xl">
              {subtitle}
            </p>
          )}
        </div>
        {actions && (
          <div className="flex flex-shrink-0 items-center gap-2">{actions}</div>
        )}
      </div>
      {beneath && <div className="mt-4">{beneath}</div>}
    </header>
  );
}
